/*
  Hero 2 (hero-v2)

  Deliberately does NOT extend the theme's SlideshowComponent: that class
  reaches out with document.querySelector for `.play-and-pause` and for the
  visible `.hero-custom-slider` slide, so two sliders on one page drive each
  other. Everything here is resolved through `this`, and every timer lives on
  the instance, so any number of Hero 2 sections can coexist on a page.
*/
(function () {
  'use strict';

  function debounce(fn, wait) {
    var timeout;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  class HeroV2Slider extends HTMLElement {
    constructor() {
      super();

      this.index = 0;
      this.autoplayTimer = null;
      this.hovered = false;
      this.focused = false;
      this.offscreen = false;
      this.userPaused = false;

      this.onScroll = debounce(this.syncFromScroll.bind(this), 100);
      this.onResize = debounce(this.scrollToIndex.bind(this, false), 200);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.hero-v2__track');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.hero-v2__slide'),
      );
      this.dots = Array.prototype.slice.call(
        this.querySelectorAll('.hero-v2__dot'),
      );
      this.prevButton = this.querySelector('.hero-v2__arrow--prev');
      this.nextButton = this.querySelector('.hero-v2__arrow--next');
      this.liveRegion = this.querySelector('.hero-v2__live-region');
      this.pauseButton = this.querySelector('.hero-v2__pause');

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.canAutoplay =
        this.dataset.autoplay === 'true' && this.slides.length > 1;

      this.bindEvents();
      this.updateUI();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (!this.track) return;

      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.observer) this.observer.disconnect();
      if (this.reducedMotion && this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onMotionChange);
      }
    }

    bindEvents() {
      this.track.addEventListener('scroll', this.onScroll, {passive: true});
      window.addEventListener('resize', this.onResize);
      this.addEventListener('keydown', this.onKeydown);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.previous.bind(this));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.next.bind(this));
      }

      this.dots.forEach(
        function (dot, i) {
          dot.addEventListener(
            'click',
            function () {
              this.goTo(i);
            }.bind(this),
          );
        }.bind(this),
      );

      // Bound even without autoplay: the button also pauses the ribbon.
      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', this.togglePause.bind(this));
      }

      if (!this.canAutoplay) return;

      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            this.offscreen = !entries[0].isIntersecting;
            this.refreshAutoplay();
          }.bind(this),
          {threshold: 0},
        );
        this.observer.observe(this);
      }
    }

    /* --------------------------------------------------------- navigation */

    get slideCount() {
      return this.slides.length;
    }

    goTo(index) {
      if (this.slideCount === 0) return;

      var count = this.slideCount;
      this.index = ((index % count) + count) % count;
      this.scrollToIndex(true);
      this.updateUI();
    }

    next() {
      this.goTo(this.index + 1);
      this.refreshAutoplay();
    }

    previous() {
      this.goTo(this.index - 1);
      this.refreshAutoplay();
    }

    scrollToIndex(animate) {
      var left = this.index * this.track.clientWidth;
      var behavior =
        animate && !this.reducedMotion.matches ? 'smooth' : 'auto';

      if (this.track.scrollTo) {
        this.track.scrollTo({left: left, behavior: behavior});
      } else {
        this.track.scrollLeft = left;
      }
    }

    syncFromScroll() {
      var width = this.track.clientWidth;
      if (!width) return;

      var index = Math.round(this.track.scrollLeft / width);
      if (index === this.index) return;

      this.index = index;
      this.updateUI();
    }

    updateUI() {
      this.slides.forEach(
        function (slide, i) {
          var current = i === this.index;
          slide.setAttribute('aria-hidden', current ? 'false' : 'true');
          // Keep off-screen slides out of the tab order.
          slide.querySelectorAll('a, button').forEach(function (el) {
            if (current) {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', '-1');
            }
          });
        }.bind(this),
      );

      this.dots.forEach(
        function (dot, i) {
          var current = i === this.index;
          dot.classList.toggle('is-active', current);
          dot.setAttribute('aria-current', current ? 'true' : 'false');
        }.bind(this),
      );

      if (this.liveRegion) {
        this.liveRegion.textContent =
          'Slide ' + (this.index + 1) + ' of ' + this.slideCount;
      }
    }

    /* ----------------------------------------------------------- autoplay */

    get shouldAutoplay() {
      return (
        this.canAutoplay &&
        !this.userPaused &&
        !this.hovered &&
        !this.focused &&
        !this.offscreen &&
        !document.hidden &&
        !this.reducedMotion.matches
      );
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.shouldAutoplay) return;

      this.autoplayTimer = setInterval(
        function () {
          this.goTo(this.index + 1);
        }.bind(this),
        this.speed,
      );
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    refreshAutoplay() {
      this.startAutoplay();
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      // The class also pauses the ribbon animation in CSS.
      this.classList.toggle('is-paused', this.userPaused);
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused
          ? this.pauseButton.dataset.labelPlay
          : this.pauseButton.dataset.labelPause,
      );
      this.refreshAutoplay();
    }

    /* ------------------------------------------------------------ handlers */

    onMouseEnter() {
      this.hovered = true;
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.hovered = false;
      this.refreshAutoplay();
    }

    onFocusIn(event) {
      // Only keyboard focus holds rotation; a mouse click on a control leaves
      // focus behind and would otherwise block autoplay from resuming.
      if (!event.target.matches(':focus-visible')) return;
      this.focused = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.refreshAutoplay();
    }

    onVisibilityChange() {
      this.refreshAutoplay();
    }

    onMotionChange() {
      this.refreshAutoplay();
    }

    onKeydown(event) {
      if (this.slideCount < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }
  }

  if (!customElements.get('hero-v2-slider')) {
    customElements.define('hero-v2-slider', HeroV2Slider);
  }

  /* Theme editor: jump to the slide the merchant selects in the sidebar. */
  document.addEventListener('shopify:block:select', function (event) {
    var slider = event.target.closest('hero-v2-slider');
    if (!slider || !slider.slides) return;

    var index = slider.slides.indexOf(event.target);
    if (index > -1) slider.goTo(index);
    slider.stopAutoplay();
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var slider = event.target.closest('hero-v2-slider');
    if (slider && slider.slides) slider.refreshAutoplay();
  });
})();
